import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, BrowserRouter, Routes, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { NavHeader } from "./components/NavHeader";
import { ImpersonationBanner } from "./components/ImpersonationBanner";

// Lazy-loaded (not a static import) deliberately: this is what gives Vite/Rollup a chunk
// boundary for the whole `/pos/*` subtree, so RxDB/dexie/html5-qrcode/the hardware
// integrations never land in the entry chunk a back-office-only (ACCOUNTANT/ECOM_MGR)
// session downloads. A single <Suspense> around the `/pos` route below covers every one of
// these, since React Router renders the matched child route inside the same tree as
// `PosLayout`'s <Outlet/> — one boundary is enough regardless of which `/pos/*` page is
// active.
const PosLayout = lazy(() => import("./pos/PosLayout"));
const TillPage = lazy(() => import("./pos/pages/TillPage").then((m) => ({ default: m.TillPage })));
const PosReturnsPage = lazy(() => import("./pos/pages/ReturnsPage").then((m) => ({ default: m.ReturnsPage })));
const MySalesPage = lazy(() => import("./pos/pages/MySalesPage").then((m) => ({ default: m.MySalesPage })));
const PrinterSetupPage = lazy(() => import("./pos/pages/PrinterSetupPage").then((m) => ({ default: m.PrinterSetupPage })));
const PosConflictsPage = lazy(() => import("./pos/pages/PosConflictsPage").then((m) => ({ default: m.PosConflictsPage })));
import { LoginPage } from "./pages/LoginPage";
import { SsoConsumePage } from "./pages/SsoConsumePage";
import { ImpersonationConsumePage } from "./pages/ImpersonationConsumePage";
import { NotAuthorizedPage } from "./pages/NotAuthorizedPage";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { MyAccountPage } from "./pages/account/MyAccountPage";
import { ExpensesPage } from "./pages/expenses/ExpensesPage";
import { DocumentGeneratorPage } from "./pages/documents/DocumentGeneratorPage";
import { GeneralLedgerPage } from "./pages/ledger/GeneralLedgerPage";
import { ChartOfAccountsPage } from "./pages/ledger/ChartOfAccountsPage";
import { NewJournalEntryPage } from "./pages/ledger/NewJournalEntryPage";
import { ProfitAndLossPage } from "./pages/ledger/ProfitAndLossPage";
import { StockPage } from "./pages/stock/StockPage";
import { InventoryDetailPage } from "./pages/inventory/InventoryDetailPage";
import { ConflictsPage } from "./pages/conflicts/ConflictsPage";
import { PaymentsPage } from "./pages/payments/PaymentsPage";
import { AssetsPage } from "./pages/assets/AssetsPage";
import { OnlineOrdersPage } from "./pages/orders/OnlineOrdersPage";
import { StaffPage } from "./pages/staff/StaffPage";
import { SuppliersPage } from "./pages/suppliers/SuppliersPage";
import { CustomersPage } from "./pages/customers/CustomersPage";
import { PurchaseOrdersPage } from "./pages/purchase-orders/PurchaseOrdersPage";
import { PurchaseOrderDetailPage } from "./pages/purchase-orders/PurchaseOrderDetailPage";
import { StockTransfersPage } from "./pages/stock-transfers/StockTransfersPage";
import { SalesDashboardPage } from "./pages/reports/SalesDashboardPage";
import { BankReconciliationPage } from "./pages/bank/BankReconciliationPage";
import { InvoicesBillsPage } from "./pages/merged/InvoicesBillsPage";
import { TaxCurrencyPage } from "./pages/merged/TaxCurrencyPage";
import { FulfillmentPage } from "./pages/merged/FulfillmentPage";
import { PromotionsPage } from "./pages/merged/PromotionsPage";
import { TeamPage } from "./pages/merged/TeamPage";
import { ApprovalsPage } from "./pages/merged/ApprovalsPage";
import { SystemPage } from "./pages/merged/SystemPage";
import {
  ROLES_WITH_APP_ACCESS,
  ROLES_WITH_APPROVALS_ACCESS,
  ROLES_WITH_ASSETS_ACCESS,
  ROLES_WITH_BANK_RECON_ACCESS,
  ROLES_WITH_BRANDING_ACCESS,
  ROLES_WITH_CONFLICTS_ACCESS,
  ROLES_WITH_CUSTOMERS_ACCESS,
  ROLES_WITH_DOCUMENTS_ACCESS,
  ROLES_WITH_EXPENSES_ACCESS,
  ROLES_WITH_INVENTORY_ACCESS,
  ROLES_WITH_INVOICE_ACCESS,
  ROLES_WITH_LEDGER_ACCESS,
  ROLES_WITH_ONLINE_ORDERS_ACCESS,
  ROLES_WITH_PAYMENTS_ACCESS,
  ROLES_WITH_PURCHASE_ORDERS_ACCESS,
  ROLES_WITH_SALES_DASHBOARD_ACCESS,
  ROLES_WITH_STAFF_ACCESS,
  ROLES_WITH_STOCK_TRANSFER_ACCESS,
  ROLES_WITH_SUPPLIERS_ACCESS,
  ROLES_WITH_TAX_FX_ACCESS,
  POS_ROLES_WITH_TILL_ACCESS,
  POS_ROLES_WITH_RETURNS_ACCESS,
  POS_ROLES_WITH_CONFLICTS_ACCESS,
} from "./roles/permissions";
import { RequireRole } from "./roles/RequireRole";

function RootRedirect() {
  const { status, user } = useAuth();
  if (status !== "authenticated" || !user) return <Navigate to="/login" replace />;
  // TELLER's only surface in this app is `/pos/*` (§10) — as of the pos-pwa merge that's
  // an in-app route, not a separate app, so TELLER lands on the till directly instead of
  // the old dead-end `/not-authorized`.
  if (user.role === "TELLER") return <Navigate to="/pos/till" replace />;
  // Business-owner walkthrough follow-up: everyone lands on the new Dashboard home page
  // now, not straight into /ledger or /inventory — see DashboardPage.tsx.
  return <Navigate to="/dashboard" replace />;
}


/** Redirects an old `/inventory/:itemId`-shaped bookmark to its new `/stock/:itemId`
 * home, preserving the id — a plain `<Navigate to="...">` can't interpolate a route
 * param, so this small wrapper reads it and builds the target itself. */
function RedirectToStockItem() {
  const { itemId } = useParams<{ itemId: string }>();
  return <Navigate to={`/stock/${itemId}`} replace />;
}

function Shell({ children }: { children: ReactNode }) {
  return <NavHeader>{children}</NavHeader>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso" element={<SsoConsumePage />} />
      <Route path="/impersonate" element={<ImpersonationConsumePage />} />
      <Route path="/not-authorized" element={<NotAuthorizedPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      <Route
        path="/account"
        element={
          <RequireRole roles={ROLES_WITH_APP_ACCESS}>
            <Shell>
              <MyAccountPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Basics (business-owner walkthrough follow-up) --- */}
      <Route
        path="/dashboard"
        element={
          <RequireRole roles={ROLES_WITH_APP_ACCESS}>
            <Shell>
              <DashboardPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/reports/pnl"
        element={
          <RequireRole roles={ROLES_WITH_LEDGER_ACCESS}>
            <Shell>
              <ProfitAndLossPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/stock"
        element={
          <RequireRole roles={ROLES_WITH_INVENTORY_ACCESS}>
            <Shell>
              <StockPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/stock/:itemId"
        element={
          <RequireRole roles={ROLES_WITH_INVENTORY_ACCESS}>
            <Shell>
              <InventoryDetailPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/reports/sales"
        element={
          <RequireRole roles={ROLES_WITH_SALES_DASHBOARD_ACCESS}>
            <Shell>
              <SalesDashboardPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/expenses"
        element={
          <RequireRole roles={ROLES_WITH_EXPENSES_ACCESS}>
            <Shell>
              <ExpensesPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/documents"
        element={
          <RequireRole roles={ROLES_WITH_DOCUMENTS_ACCESS}>
            <Shell>
              <DocumentGeneratorPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/staff"
        element={
          <RequireRole roles={ROLES_WITH_STAFF_ACCESS}>
            <Shell>
              <StaffPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/orders"
        element={
          <RequireRole roles={ROLES_WITH_ONLINE_ORDERS_ACCESS}>
            <Shell>
              <OnlineOrdersPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Advanced: Accounting --- */}
      <Route
        path="/ledger"
        element={
          <RequireRole roles={ROLES_WITH_LEDGER_ACCESS}>
            <Shell>
              <GeneralLedgerPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/ledger/new"
        element={
          <RequireRole roles={ROLES_WITH_LEDGER_ACCESS}>
            <Shell>
              <NewJournalEntryPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/accounts"
        element={
          <RequireRole roles={ROLES_WITH_LEDGER_ACCESS}>
            <Shell>
              <ChartOfAccountsPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/invoices"
        element={
          <RequireRole roles={ROLES_WITH_INVOICE_ACCESS}>
            <Shell>
              <InvoicesBillsPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/tax-currency"
        element={
          <RequireRole roles={ROLES_WITH_TAX_FX_ACCESS}>
            <Shell>
              <TaxCurrencyPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/payments"
        element={
          <RequireRole roles={ROLES_WITH_PAYMENTS_ACCESS}>
            <Shell>
              <PaymentsPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/bank-reconciliation"
        element={
          <RequireRole roles={ROLES_WITH_BANK_RECON_ACCESS}>
            <Shell>
              <BankReconciliationPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Advanced: Inventory & Supply --- */}
      <Route
        path="/purchase-orders"
        element={
          <RequireRole roles={ROLES_WITH_PURCHASE_ORDERS_ACCESS}>
            <Shell>
              <PurchaseOrdersPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/purchase-orders/:poId"
        element={
          <RequireRole roles={ROLES_WITH_PURCHASE_ORDERS_ACCESS}>
            <Shell>
              <PurchaseOrderDetailPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/stock-transfers"
        element={
          <RequireRole roles={ROLES_WITH_STOCK_TRANSFER_ACCESS}>
            <Shell>
              <StockTransfersPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/suppliers"
        element={
          <RequireRole roles={ROLES_WITH_SUPPLIERS_ACCESS}>
            <Shell>
              <SuppliersPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Advanced: Sales & Customers --- */}
      <Route
        path="/customers"
        element={
          <RequireRole roles={ROLES_WITH_CUSTOMERS_ACCESS}>
            <Shell>
              <CustomersPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/fulfillment"
        element={
          <RequireRole roles={ROLES_WITH_APP_ACCESS}>
            <Shell>
              <FulfillmentPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/promotions"
        element={
          <RequireRole roles={ROLES_WITH_APP_ACCESS}>
            <Shell>
              <PromotionsPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Advanced: Team & Access --- */}
      <Route
        path="/team"
        element={
          <RequireRole roles={ROLES_WITH_STAFF_ACCESS}>
            <Shell>
              <TeamPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Advanced: Operations --- */}
      <Route
        path="/approvals"
        element={
          <RequireRole roles={ROLES_WITH_APPROVALS_ACCESS}>
            <Shell>
              <ApprovalsPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/conflicts"
        element={
          <RequireRole roles={ROLES_WITH_CONFLICTS_ACCESS}>
            <Shell>
              <ConflictsPage />
            </Shell>
          </RequireRole>
        }
      />
      <Route
        path="/assets"
        element={
          <RequireRole roles={ROLES_WITH_ASSETS_ACCESS}>
            <Shell>
              <AssetsPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- Advanced: System (OWNER only) --- */}
      <Route
        path="/system"
        element={
          <RequireRole roles={ROLES_WITH_BRANDING_ACCESS}>
            <Shell>
              <SystemPage />
            </Shell>
          </RequireRole>
        }
      />

      {/* --- /pos/* : the offline-first till, merged in from the old standalone
          `pos-pwa` app. Layout-gated on POS_ROLES_WITH_TILL_ACCESS (coarse "can this role
          even reach /pos" check) with each child route additionally RequireRole-gated for
          the finer-grained cases, exactly as pos-pwa/src/App.tsx gated them standalone. --- */}
      <Route
        path="/pos"
        element={
          <RequireRole roles={POS_ROLES_WITH_TILL_ACCESS}>
            <Suspense fallback={<div className="page">Loading…</div>}>
              <PosLayout />
            </Suspense>
          </RequireRole>
        }
      >
        <Route index element={<Navigate to="till" replace />} />
        <Route path="till" element={<TillPage />} />
        <Route
          path="returns"
          element={
            <RequireRole roles={POS_ROLES_WITH_RETURNS_ACCESS}>
              <PosReturnsPage />
            </RequireRole>
          }
        />
        <Route
          path="conflicts"
          element={
            <RequireRole roles={POS_ROLES_WITH_CONFLICTS_ACCESS}>
              <PosConflictsPage />
            </RequireRole>
          }
        />
        <Route path="my-sales" element={<MySalesPage />} />
        <Route path="printer-setup" element={<PrinterSetupPage />} />
      </Route>

      {/* --- Redirects for every pre-redesign URL, so no old bookmark 404s --- */}
      <Route path="/inventory" element={<Navigate to="/stock?tab=all" replace />} />
      <Route path="/inventory/bulk" element={<Navigate to="/stock?tab=import-export" replace />} />
      <Route path="/inventory/:itemId" element={<RedirectToStockItem />} />
      <Route path="/reports/low-stock" element={<Navigate to="/stock?tab=low-stock" replace />} />
      <Route path="/reports/expiring-soon" element={<Navigate to="/stock?tab=expiring" replace />} />
      <Route path="/invoices/aging" element={<Navigate to="/invoices?tab=aging" replace />} />
      <Route path="/tax-rates" element={<Navigate to="/tax-currency?tab=rates" replace />} />
      <Route path="/exchange-rates" element={<Navigate to="/tax-currency?tab=exchange" replace />} />
      <Route path="/returns" element={<Navigate to="/fulfillment?tab=returns" replace />} />
      <Route path="/shipments" element={<Navigate to="/fulfillment?tab=shipments" replace />} />
      <Route path="/promo-codes" element={<Navigate to="/promotions?tab=promo-codes" replace />} />
      <Route path="/gift-cards" element={<Navigate to="/promotions?tab=gift-cards" replace />} />
      <Route path="/loyalty" element={<Navigate to="/promotions?tab=loyalty" replace />} />
      <Route path="/branches" element={<Navigate to="/team?tab=branches" replace />} />
      <Route path="/permissions" element={<Navigate to="/team?tab=permissions" replace />} />
      <Route path="/approvals/rules" element={<Navigate to="/approvals?tab=rules" replace />} />
      <Route path="/notifications-log" element={<Navigate to="/system?tab=notifications" replace />} />
      <Route path="/webhooks" element={<Navigate to="/system?tab=webhooks" replace />} />
      <Route path="/settings/branding" element={<Navigate to="/system?tab=branding" replace />} />

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
